/**
 * HOW MANY PICTURES ON THE EDITOR ARE STILL GOING IN - the one number "Send to an editor" waits on.
 *
 * Each picture control owns its own upload (./use-article-image-upload.ts), so the screen could not
 * see one in flight, and Send sealed the draft without it: the claim then answered on an article
 * already in review, and the control cancelled it. Now every control counts itself in here while
 * its phase is `uploading` or `claiming`, and ../EditorRoute.tsx holds Send while the count is not
 * zero - saying why beside the button, because a write can take a minute to fail offline and 90 s
 * to be stopped when it hangs, and a Send that waited quietly would look broken.
 *
 * ★ A HOLD IS TAKEN IN AN EFFECT'S BODY AND DROPPED BY ITS CLEANUP, AND NOWHERE ELSE. StrictMode
 * tears every effect down and sets it up again in development (src/main.tsx), and `<Activity>` does
 * the same on every hide and show; a hold, or a "registered" flag, set anywhere but the effect's own
 * body would be lost or doubled by that cycle. One token per hold, so a release run twice is one.
 */
import { createContext, useContext, useSyncExternalStore } from 'react';

export interface UploadsInFlight {
  /** How many pictures are uploading or being claimed right now. */
  readonly count: () => number;
  /** Counts one picture in until the returned release is called. */
  readonly hold: () => () => void;
  /** For `useSyncExternalStore`. */
  readonly subscribe: (onChange: () => void) => () => void;
}

export function createUploadsInFlight(): UploadsInFlight {
  const held = new Set<symbol>();
  const listeners = new Set<() => void>();
  const changed = (): void => {
    for (const listener of listeners) {
      listener();
    }
  };
  return {
    count: () => held.size,
    hold: () => {
      const token = Symbol('a picture going in');
      held.add(token);
      changed();
      return () => {
        if (held.delete(token)) {
          changed();
        }
      };
    },
    subscribe: (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
  };
}

const UploadsInFlightContext = createContext<UploadsInFlight | null>(null);

/** One per screen, provided beside `EditorUploaderProvider` by ../EditorRoute.tsx. */
export const UploadsInFlightProvider = UploadsInFlightContext.Provider;

export function useUploadsInFlight(): UploadsInFlight {
  const uploads = useContext(UploadsInFlightContext);
  if (uploads === null) {
    // Refused rather than skipped: a picture nobody counts is one Send would seal the draft without.
    throw new Error(
      'No UploadsInFlight in context. The editor screen provides one for its own subtree - see ' +
        'routes/articles/EditorRoute.tsx - so that Send can wait for a picture still going in.',
    );
  }
  return uploads;
}

/** The count, re-rendering the caller whenever it moves. */
export function useUploadsInFlightCount(uploads: UploadsInFlight): number {
  return useSyncExternalStore(uploads.subscribe, uploads.count);
}
